import IconCar from "@simble/shared/assets/icons/car-ico.svg?react";
import IconClose from "@simble/shared/assets/icons/close-ico.svg?react";
import IconError from "@simble/shared/assets/icons/error-ico.svg?react";
import IconNotification from "@simble/shared/assets/icons/finuslugi/notification-ico.svg?react";
import IconSearch from "@simble/shared/assets/icons/search-ico.svg?react";
import useCarSearch from "@simble/shared/hooks/use-car-search";
import { usePartnerStatistics } from "@simble/shared/hooks/use-partner-statistics";
import { usePartnersConfirmation } from "@simble/shared/hooks/use-partners-confirmation";
import { checkSberParkingRatesAvailable } from "@simble/shared/hooks/use-poffers-api";
import { getProfileCompanyReview, postApplication, putConnectCognito } from "@simble/shared/hooks/use-profile-api";
import { getCar, getVehicleCompanyReview, postOwn, postOwnApplication, postRejectApplication } from "@simble/shared/hooks/use-vehicles-api";
import { calculateAge } from "@simble/shared/lib/calculate-age";
import { getCarHash, getCarMakeNormalized, getCarModelNormalized, isCarMakeModelIncomplete } from "@simble/shared/lib/car-hash";
import { cn } from "@simble/shared/lib/cn";
import { registerCognitoUserByUsername } from "@simble/shared/lib/cognito-register";
import { getCookie } from "@simble/shared/lib/cookie-utils";
import { formatRegNumber } from "@simble/shared/lib/format-grz-number";
import { sendMetricaGoal } from "@simble/shared/lib/metrica";
import Button from "@simble/ui/button";
import Checkbox from "@simble/ui/checkbox";
import GRZMaskInput from "@simble/ui/grz-mask-input";
import MaskInput from "@simble/ui/mask-input";
import { showToast } from "@simble/ui/toast";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { parse } from "date-fns";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "motion/react";
import React, { useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { CarConfirmationDrawer } from "@/components/car-confirmation-drawer";
import Header from "@/components/header";
import { ProfileCreatedShareCard } from "@/components/profile-created-share-card";
import { SendLinkErrorDrawer } from "@/components/send-link-error-drawer";
import { dateConfig, fioMask, getDateOfBirthConfig, getFioConfig, licenseNumberConfig, phoneConfig } from "@/configs/mask-configs";
import { finuslugiMaskInputProps } from "@/constants/mask-input-styles";
import PageLayout from "@/layouts/page-layout";
import { store } from "@/store/local-store";
export const Route = createFileRoute("/registration-data")({
  component: RegistrationData,
});

const MIN_GRZ_LENGTH = 8;
const SPACING_REGULAR = /\s+/;
const LOADING_BUTTON_TEXTS = ["Проверяем корректность...", "Сверяем авто...", "Сверяем водителя...", "Завершаем оформление..."];

const normalizePhone = (phone: string | undefined): string => {
  if (!phone) {
    return "";
  }
  return phone.startsWith("+7") ? phone.slice(2) : phone;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <пока что так, потом надо вынести маски в отдельные компоненты>
function RegistrationData() {
  const grzInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const dateOfBirthInputRef = useRef<HTMLInputElement>(null);
  const licenseNumberInputRef = useRef<HTMLInputElement>(null);
  const licenseIssueDateInputRef = useRef<HTMLInputElement>(null);
  const experienceStartDateInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const partnerIdForStatistics = store.partnerId.peek() ?? null;
  usePartnerStatistics(partnerIdForStatistics);
  const [isNoSurName, setIsNoSurName] = React.useState(store.noSurname.peek() ?? false);
  const [isProfileCreated, setIsProfileCreated] = React.useState(false);
  const [isExistingProfile, setIsExistingProfile] = React.useState(false);
  const [isInsuranceRejected, setIsInsuranceRejected] = React.useState(false);
  const [isSendingLink, setIsSendingLink] = React.useState(false);
  const [loadingTextIndex, setLoadingTextIndex] = React.useState(0);
  const [displayState, setDisplayState] = React.useState({
    text: LOADING_BUTTON_TEXTS[0],
    key: "0",
  });
  const lastUpdateRef = React.useRef(0);

  const targetState = React.useMemo(() => {
    return { text: LOADING_BUTTON_TEXTS[loadingTextIndex], key: loadingTextIndex.toString() };
  }, [loadingTextIndex]);

  React.useEffect(() => {
    if (isSendingLink && lastUpdateRef.current === 0) {
      lastUpdateRef.current = Date.now();
    } else if (!isSendingLink) {
      lastUpdateRef.current = 0;
      setDisplayState({ text: LOADING_BUTTON_TEXTS[0], key: "0" });
    }
  }, [isSendingLink]);

  React.useEffect(() => {
    const minDuration = 1500;
    const now = Date.now();
    const timeSinceLast = now - lastUpdateRef.current;

    if (displayState.key === targetState.key) {
      return;
    }

    const timeRemaining = Math.max(0, minDuration - timeSinceLast);

    const timeout = setTimeout(() => {
      setDisplayState(targetState);
      lastUpdateRef.current = Date.now();
    }, timeRemaining);

    return () => clearTimeout(timeout);
  }, [targetState, displayState]);
  const [grzKey, setGrzKey] = React.useState(0);
  const [formUiKey, setFormUiKey] = React.useState(0);
  const [isErrorDrawerOpen, setIsErrorDrawerOpen] = React.useState(false);
  const checkDatesMatch = React.useCallback((passportDate: string | undefined, experienceStartDate: string | undefined): boolean => {
    if (!(passportDate && experienceStartDate)) {
      return false;
    }
    const passportDateNormalized = passportDate.replace(/\./g, "");
    const experienceStartDateNormalized = experienceStartDate.replace(/\./g, "");
    return passportDateNormalized === experienceStartDateNormalized && passportDateNormalized.length > 0;
  }, []);
  const [isExperienceDateSame, setIsExperienceDateSame] = React.useState(
    checkDatesMatch(store.passportDate.peek(), store.experienceStartDate.peek())
  );

  const methods = useForm({
    defaultValues: {
      grz: store.grz.peek() ?? "",
      name: store.fio.peek() ?? "",
      dateOfBirth: (store.dateOfBirth.peek() ?? "").replace(/\./g, ""),
      licenseNumber: (store.passport.peek() ?? "").replace(/\s/g, ""),
      licenseIssueDate: (store.passportDate.peek() ?? "").replace(/\./g, ""),
      experienceStartDate: (store.experienceStartDate.peek() ?? "").replace(/\./g, ""),
      phone: normalizePhone(store.phoneNum.peek()),
    },
    mode: "onChange",
  });

  const partnersConfirmationMutation = usePartnersConfirmation();

  const selectedCar = store.selectedCar.peek();

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <NEED REFACTOR>
  // biome-ignore lint/correctness/useExhaustiveDependencies: <NEED REFACTOR>
  const handleSendLink = React.useCallback(async () => {
    methods.clearErrors();
    sendMetricaGoal("click_create_profile");
    const grz = methods.getValues("grz");
    const name = methods.getValues("name");
    const dateOfBirth = methods.getValues("dateOfBirth");
    const licenseNumber = methods.getValues("licenseNumber");
    const licenseIssueDate = methods.getValues("licenseIssueDate");
    const experienceStartDate = methods.getValues("experienceStartDate");
    const phone = methods.getValues("phone");
    const partnerId = store.partnerId.peek();

    let hasErrors = false;
    let hasGrzError = false;

    if (!selectedCar && (!grz || grz.length < MIN_GRZ_LENGTH)) {
      methods.setError("grz", { message: "Введите госномер и нажмите «Найти авто»" });
      hasErrors = true;
      hasGrzError = true;
    }

    if (!displayCar) {
      methods.setError("grz", { message: "Введите госномер и нажмите «Найти авто»" });
      hasErrors = true;
      hasGrzError = true;
    }

    if (!name || name.trim() === "") {
      methods.setError("name", { message: "Заполните поле" });
      hasErrors = true;
    } else {
      const preparedName = name
        .split("")
        .map((char) => fioMask.prepare(char))
        .join("");

      if (fioMask.mask.test(preparedName)) {
        const words = preparedName
          .trim()
          // biome-ignore lint/performance/useTopLevelRegex: <NEED REFACTOR>
          .split(/\s+/)
          .filter((word) => word.length > 0);
        const expectedWordCount = isNoSurName ? 2 : 3;

        if (words.length !== expectedWordCount) {
          let errorMessage = "";
          if (words.length === 1) {
            errorMessage = isNoSurName ? "Заполните поле (нужно ввести имя)" : "Заполните поле (нужно ввести имя и отчество)";
          } else if (words.length === 2 && !isNoSurName) {
            errorMessage = "Заполните поле (нужно ввести отчество)";
          } else {
            errorMessage = isNoSurName ? "Проверьте правильность данных" : "Проверьте правильность данных";
          }
          methods.setError("name", { message: errorMessage });
          hasErrors = true;
        }
      } else {
        methods.setError("name", { message: "Проверьте правильность данных" });
        hasErrors = true;
      }
    }

    if (!dateOfBirth || dateOfBirth.length !== 8) {
      methods.setError("dateOfBirth", { message: "Введите дату рождения" });
      hasErrors = true;
    }

    const validationResult = validateDateOfBirth(dateOfBirth);
    if (validationResult && typeof validationResult === "string") {
      methods.setError("dateOfBirth", { message: validationResult });
      hasErrors = true;
    }

    if (!licenseNumber || licenseNumber.length < 10) {
      methods.setError("licenseNumber", { message: "Укажите серию и номер прав" });
      hasErrors = true;
    }

    if (!licenseIssueDate || licenseIssueDate.length !== 8) {
      methods.setError("licenseIssueDate", { message: "Введите дату выдачи прав" });
      hasErrors = true;
    } else {
      const licenseIssueDateValidation = validateDate(licenseIssueDate);
      if (licenseIssueDateValidation && typeof licenseIssueDateValidation === "string") {
        methods.setError("licenseIssueDate", { message: licenseIssueDateValidation });
        hasErrors = true;
      }
    }

    if (!isExperienceDateSame) {
      if (!experienceStartDate || experienceStartDate.length !== 8) {
        methods.setError("experienceStartDate", { message: "Укажите дату начала стажа" });
        hasErrors = true;
      } else {
        const experienceStartDateValidation = validateDate(experienceStartDate);
        if (experienceStartDateValidation && typeof experienceStartDateValidation === "string") {
          methods.setError("experienceStartDate", { message: experienceStartDateValidation });
          hasErrors = true;
        }
      }
    }

    const normalizedPhone = phone?.replace(/[^0-9]/g, "") || "";
    if (!phone || phone.trim() === "" || normalizedPhone.length < 10) {
      methods.setError("phone", { message: "Введите номер телефона клиента" });
      hasErrors = true;
    }

    const dateOfBirthFormatted =
      dateOfBirth.length === 8 ? `${dateOfBirth.slice(0, 2)}.${dateOfBirth.slice(2, 4)}.${dateOfBirth.slice(4, 8)}` : dateOfBirth;
    const licenseIssueDateFormatted =
      licenseIssueDate.length === 8
        ? `${licenseIssueDate.slice(0, 2)}.${licenseIssueDate.slice(2, 4)}.${licenseIssueDate.slice(4, 8)}`
        : licenseIssueDate;
    const experienceStartDateFormatted =
      experienceStartDate && experienceStartDate.length === 8
        ? `${experienceStartDate.slice(0, 2)}.${experienceStartDate.slice(2, 4)}.${experienceStartDate.slice(4, 8)}`
        : experienceStartDate;

    // const birthDate = parseBirthDate(dateOfBirthFormatted);

    const age = calculateAge(dateOfBirthFormatted);
    const licenseIssueDateParsed = parse(licenseIssueDateFormatted, "dd.MM.yyyy", new Date());
    const today = dayjs();
    const yearsSinceLicenseIssue = today.diff(dayjs(licenseIssueDateParsed), "year", true);

    if (yearsSinceLicenseIssue > 13) {
      methods.setError("licenseIssueDate", { message: "Не более 13 лет с текущей даты" });
      hasErrors = true;
    }

    const actualExperienceStartDate = isExperienceDateSame ? licenseIssueDateFormatted : experienceStartDateFormatted;
    if (actualExperienceStartDate) {
      const experienceStartDateParsed = parse(actualExperienceStartDate, "dd.MM.yyyy", new Date());
      const experienceYears = today.diff(dayjs(experienceStartDateParsed), "year", true);

      if (experienceYears < 1) {
        const experienceError = "Страхование доступно при стаже от года";
        methods.setError("licenseIssueDate", { message: experienceError });
        if (!isExperienceDateSame) {
          methods.setError("experienceStartDate", { message: experienceError });
        }
        hasErrors = true;
      }

      if (experienceYears > age) {
        const ageError = "Стаж не соответствует возрасту";
        methods.setError("licenseIssueDate", { message: ageError });
        if (!isExperienceDateSame) {
          methods.setError("experienceStartDate", { message: ageError });
        }
        hasErrors = true;
      }

      const experienceAfterLicense = !isExperienceDateSame && dayjs(experienceStartDateParsed).isAfter(dayjs(licenseIssueDateParsed));
      if (experienceAfterLicense) {
        const afterError = "Дата выдачи последних прав не соответствует началу стажа";
        methods.setError("licenseIssueDate", { message: afterError });
        methods.setError("experienceStartDate", { message: afterError });
        hasErrors = true;
      }
    }

    if (hasErrors) {
      if (hasGrzError && grzInputRef.current) {
        requestAnimationFrame(() => {
          grzInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      }
      return;
    }

    if (!partnerId) {
      showToast({ title: "Не удалось получить данные партнера" });
      return;
    }

    const phoneNumber = phone.startsWith("+7") ? phone : `+7${phone}`;
    const handleExistingProfileFound = () => {
      setIsExistingProfile(true);
    };

    setIsSendingLink(true);
    setLoadingTextIndex(0);

    const authToken = getCookie("auth_token");

    const toDdMmYyyy = (s: string | undefined) => (s && s.length === 8 ? `${s.slice(0, 2)}.${s.slice(2, 4)}.${s.slice(4, 8)}` : (s ?? ""));
    const expStart = methods.getValues("experienceStartDate");
    const licIssue = methods.getValues("licenseIssueDate");

    const sberExperienceDateStr = isExperienceDateSame ? toDdMmYyyy(licIssue) : toDdMmYyyy(expStart);

    const drivingExperienceDate = (() => {
      if (!sberExperienceDateStr) {
        return dayjs().format("YYYY-MM-DD");
      }
      const d = dayjs(parse(sberExperienceDateStr, "dd.MM.yyyy", new Date()));
      return d.isValid() ? d.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
    })();

    try {
      let uid: string | undefined;
      try {
        uid = await partnersConfirmationMutation.mutateAsync({
          phone_number: phoneNumber,
          partner_id: partnerId.toString(),
          channel_id: Number.parseInt(import.meta.env.VITE_PARTNER_CHANNEL_ID || "0", 10),
        });
      } catch (confirmationError) {
        if (isAxiosError(confirmationError) && confirmationError.response?.status === 400) {
          handleExistingProfileFound();
          return;
        }
        throw confirmationError;
      }

      setLoadingTextIndex(1);

      if (!(authToken && selectedCar && uid)) {
        showToast({ title: "Не удалось получить данные пользователя" });
        return;
      }

      const grzValue = methods.getValues("grz");
      const carResponse = await getCar({
        uid,
        plate: grzValue,
        channelId: Number.parseInt(import.meta.env.VITE_PARTNER_CHANNEL_ID || "0", 10),
        config: {
          headers: {
            Authorization: authToken,
          },
        },
      });

      const typedResponse = carResponse as { data: Array<{ id: string }>; status: number };
      let carId = typedResponse && Array.isArray(typedResponse.data) && typedResponse.data.length > 0 ? typedResponse.data[0]?.id : null;

      if (!carId) {
        carId = await getCarHash(selectedCar);

        await postOwn({
          params: { uid, id: carId },
          body: {
            year: selectedCar?.year,
            model: getCarModelNormalized(selectedCar),
            make: getCarMakeNormalized(selectedCar),
            plate: selectedCar?.identifiers?.reg_num
              ? {
                  value: selectedCar.identifiers.reg_num,
                  type: "russia93",
                }
              : {
                  value: "",
                  type: "russia93",
                },
            channel_id: Number.parseInt(import.meta.env.VITE_PARTNER_CHANNEL_ID || "0", 10),
          },
          config: {
            headers: {
              Authorization: authToken,
            },
          },
        });

        const vehicleDocs = selectedCar.sbs_data?.vehicle?.vehicleDocuments;
        const sts_date = vehicleDocs?.find((doc) => doc.type === "STS")?.issueDate?.split(" ")[0];
        const pts_doc = vehicleDocs?.find((doc) => doc.type === "PTS" || doc.type === "EPTS");
        const pts_number = pts_doc?.number;
        const pts_series = pts_doc?.series;
        const pts_issue_date = pts_doc?.issueDate?.split(" ")[0];

        await postOwnApplication({
          params: { uid, id: carId },
          body: {
            data: {
              type: "simble2022-12",
              registration: {
                vin: selectedCar?.identifiers?.vin ? selectedCar.identifiers.vin.replace(/\s+/g, "") : undefined,
                registration_number: selectedCar?.identifiers?.sts ?? undefined,
                issue_date: sts_date ?? "1970-01-01",
              },
              plate: {
                value: selectedCar?.identifiers?.reg_num ?? "",
                type: "russia93",
              },
              ...(!!pts_number && !!pts_issue_date
                ? {
                    passport: {
                      number: `${pts_series || ""}${pts_number || ""}`,
                      issue_date: pts_issue_date,
                    },
                  }
                : {}),
            },
          },
          config: {
            params: {
              submit: false,
              channel_id: Number.parseInt(import.meta.env.VITE_PARTNER_CHANNEL_ID || "0", 10),
              version: 0,
            },
            headers: {
              Authorization: authToken,
            },
          },
        });

        const licenseIssueDateMethods = methods.getValues("licenseIssueDate");
        if (!licenseIssueDateMethods) {
          return;
        }

        setLoadingTextIndex(2);

        const passDate =
          licenseIssueDateMethods.length === 8
            ? `${licenseIssueDateMethods.slice(0, 2)}.${licenseIssueDateMethods.slice(2, 4)}.${licenseIssueDateMethods.slice(4, 8)}`
            : licenseIssueDateMethods;

        await postApplication(
          uid,
          {
            data: {
              type: "simble2022-12",
              license: {
                type: "russia2019",
                number: methods.getValues("licenseNumber")?.replaceAll(" ", "") ?? "",
                issue_date: passDate.split(".").reverse().join("-"),
              },
              id: {
                is_without_given_name: store.noSurname.peek() ?? false,
                gender: "male",
                full_name: methods.getValues("name") ?? "",
                date_of_birth: (() => {
                  const dateOfBirthMethods = methods.getValues("dateOfBirth");
                  if (!dateOfBirthMethods) {
                    return undefined;
                  }
                  const formattedDate =
                    dateOfBirthMethods.length === 8
                      ? `${dateOfBirthMethods.slice(0, 2)}.${dateOfBirthMethods.slice(2, 4)}.${dateOfBirthMethods.slice(4, 8)}`
                      : dateOfBirthMethods;
                  return formattedDate.split(".").reverse().join("-");
                })(),
              },
              driving_experience: drivingExperienceDate,
            },
          },
          {
            params: {
              submit: true,
              channel_id: Number.parseInt(import.meta.env.VITE_PARTNER_CHANNEL_ID || "0", 10),
              version: 0,
            },
            headers: {
              Authorization: authToken,
            },
          }
        );
      }

      setLoadingTextIndex(2);

      // Валидация страховой: company review профиля и ТС; parking — отдельно (пустой → reject, флоу не блокируем)
      const authHeaders = { headers: { Authorization: authToken } };
      let profilePassed = false;
      let vehiclePassed = false;
      let ratesPassed = false;
      const forceVehicleRejectIncomplete = isCarMakeModelIncomplete(selectedCar);
      const parkingRatesRequest = {
        vehicle: {
          make: getCarMakeNormalized(selectedCar),
          model: getCarModelNormalized(selectedCar),
          ...(selectedCar.year == null ? {} : { year: selectedCar.year }),
        },
        user: {
          age,
          driving_experience: Math.max(0, Math.floor(dayjs().diff(dayjs(drivingExperienceDate), "year", true))),
          gender: "male",
        },
        channel_id: Number.parseInt(import.meta.env.VITE_PARTNER_CHANNEL_ID || "0", 10),
        lifetime: 7 * 24 * 60,
      };
      try {
        if (forceVehicleRejectIncomplete) {
          const [profileReview, hasParkingRates] = await Promise.all([
            getProfileCompanyReview(uid, authHeaders),
            checkSberParkingRatesAvailable(parkingRatesRequest),
          ]);
          profilePassed = profileReview.result === true;
          vehiclePassed = false;
          ratesPassed = hasParkingRates;
        } else {
          const [profileReview, vehicleReview, hasParkingRates] = await Promise.all([
            getProfileCompanyReview(uid, authHeaders),
            getVehicleCompanyReview(carId, authHeaders),
            checkSberParkingRatesAvailable(parkingRatesRequest),
          ]);
          profilePassed = profileReview.result === true;
          vehiclePassed = vehicleReview.result === true;
          ratesPassed = hasParkingRates;
        }
      } catch {
        profilePassed = false;
        vehiclePassed = false;
        ratesPassed = false;
      }

      const fullValidationPassed = profilePassed && vehiclePassed;
      /** Валидный профиль + невалидное ТС: одобрение по заявке (не отказ по профилю) */
      const profileOkVehicleRejected = profilePassed && !vehiclePassed;
      /** Отказ по заявке: не прошла проверка профиля (включая оба отказа) */
      const insuranceRejectedCase = !profilePassed;
      /** Пустые тарифы parking или провал review — reject заявки */
      const shouldRejectApplication = !(fullValidationPassed && ratesPassed);

      setLoadingTextIndex(3);

      /** Cognito до UI отказа: если аккаунт уже есть, сразу показываем «Профиль уже существует», без мигания «Заявка не одобрена». */
      const cognitoResult = await registerCognitoUserByUsername({
        clientId: import.meta.env.VITE_USER_POOL_WEB_CLIENT_ID,
        username: phoneNumber,
        phoneNumber,
        options: {
          region: import.meta.env.VITE_REGION,
        },
      });

      if (cognitoResult.status === "exists") {
        if (shouldRejectApplication) {
          try {
            await postRejectApplication({ vid: carId, config: authHeaders });
          } catch {
            // Игнорируем: заявка могла не быть создана (например, авто уже существовало)
          }
        }
        handleExistingProfileFound();
        return;
      }

      if (shouldRejectApplication) {
        try {
          await postRejectApplication({ vid: carId, config: authHeaders });
        } catch {
          // Игнорируем: заявка могла не быть создана (например, авто уже существовало)
        }
      }

      if (!fullValidationPassed) {
        /** Не открываем экран отказа по ТС, если профиль одобрен — показываем успешное создание профиля и МП */
        if (!profileOkVehicleRejected) {
          setShowRatesInvalid(true);
        }
        if (insuranceRejectedCase) {
          setIsInsuranceRejected(true);
        }
      }

      if (cognitoResult.status === "created" && cognitoResult.userSub && authToken) {
        await putConnectCognito(uid, { username: cognitoResult.userSub }, { headers: { Authorization: authToken } });
      }

      if (!(fullValidationPassed || profileOkVehicleRejected)) {
        return;
      }

      setTimeout(() => {
        setIsProfileCreated(true);
        sendMetricaGoal("web_view_success");
      }, 0);
    } catch (error) {
      console.error("Ошибка отправки подтверждения:", error);
      setIsErrorDrawerOpen(true);
    } finally {
      setIsSendingLink(false);
    }
  }, [methods, isExperienceDateSame, isNoSurName, selectedCar]);

  const handleRetrySendLink = React.useCallback(() => {
    setIsErrorDrawerOpen(false);
    handleSendLink();
  }, [handleSendLink]);

  const handleRegisterAnotherClient = React.useCallback(() => {
    const currentGrz = methods.getValues("grz");
    methods.reset({
      grz: "",
      name: "",
      dateOfBirth: "",
      licenseNumber: "",
      licenseIssueDate: "",
      experienceStartDate: "",
      phone: "",
    });
    methods.clearErrors();
    setIsExistingProfile(false);
    setIsInsuranceRejected(false);
    setIsProfileCreated(false);
    setIsNoSurName(false);
    setIsExperienceDateSame(false);
    setWasSearched(false);
    setIsDrawerOpen(false);
    setShowRatesInvalid(false);
    if (currentGrz) {
      queryClient.setQueryData(["search-car", "grz", currentGrz], null);
      queryClient.removeQueries({ queryKey: ["search-car", "grz", currentGrz] });
    }
    setGrzKey((prev) => prev + 1);
    setFormUiKey((prev) => prev + 1);
    store.assign({
      grz: "",
      fio: "",
      dateOfBirth: "",
      passport: "",
      passportDate: "",
      experienceStartDate: "",
      phoneNum: "",
      noSurname: false,
      selectedCar: null,
    });
  }, [methods, queryClient]);

  const grzValue = methods.watch("grz") || "";

  const [wasSearched, setWasSearched] = React.useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [showRatesInvalid, setShowRatesInvalid] = React.useState(false);
  const {
    data: carData,
    isFetching,
    isFetched,
    error: carError,
    refetch: refetchCar,
  } = useCarSearch("grz", grzValue, "ru", {
    enabled: false,
  });

  const displayCar = carData ?? selectedCar;

  React.useEffect(() => {
    if (!wasSearched || isFetching) {
      return;
    }
    if (carData) {
      const carInvalid = (carData as { is_valid?: boolean })?.is_valid === false;
      if (carInvalid) {
        setIsDrawerOpen(true);
        return;
      }
      setIsDrawerOpen(true);
      return;
    }
    if (carError) {
      queryClient.setQueryData(["search-car", "grz", grzValue], null);
      store.selectedCar.set(null);
      setIsDrawerOpen(true);
      return;
    }
    if (isFetched && !carData && !carError) {
      setIsDrawerOpen(true);
    }
  }, [wasSearched, isFetching, carData, isFetched, carError, grzValue, queryClient]);

  React.useEffect(() => {
    sendMetricaGoal("web_view_data");
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (carData) {
      const carInvalid = (carData as { is_valid?: boolean })?.is_valid === false;
      if (carInvalid) {
        store.selectedCar.set(null);
        return;
      }
      store.selectedCar.set(carData);
    }
  }, [carData]);

  const carName = (() => {
    if (!displayCar) {
      return "";
    }
    const make = getCarMakeNormalized(displayCar);
    const model = getCarModelNormalized(displayCar);
    if (make && model && displayCar.year != null) {
      return `${make} ${model}, ${displayCar.year}`;
    }
    if (displayCar.year != null) {
      return `Автомобиль, ${displayCar.year}`;
    }
    return "Автомобиль";
  })();
  const carRegNumber =
    displayCar?.identifiers?.reg_num != null && displayCar.identifiers.reg_num !== "" ? formatRegNumber(displayCar.identifiers.reg_num) : "";

  const [grzError, setGrzError] = React.useState<string | null>(null);
  const [isNoSurNameError, setIsNoSurNameError] = React.useState<string | undefined>(undefined);

  const handleSearchCar = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      sendMetricaGoal("click_find_car");
      if (!grzValue || grzValue.length < MIN_GRZ_LENGTH) {
        setGrzError("Заполните поле полностью");
        return;
      }
      setGrzError(null);
      setWasSearched(true);
      refetchCar();
    },
    [grzValue, refetchCar]
  );

  const handleCarConfirm = React.useCallback(() => {
    setWasSearched(false);
  }, []);

  const handleClearCar = React.useCallback(() => {
    store.selectedCar.set(null);
    if (grzValue) {
      queryClient.setQueryData(["search-car", "grz", grzValue], null);
      queryClient.removeQueries({ queryKey: ["search-car", "grz", grzValue] });
    }
    queryClient.setQueryData(["search-car", "grz", grzValue], null);
    methods.resetField("grz", { defaultValue: "" });
    setGrzKey((prev) => prev + 1);
    setWasSearched(false);
    setIsDrawerOpen(false);
  }, [grzValue, queryClient, methods]);

  const dateOfBirthConfig = React.useMemo(() => getDateOfBirthConfig(), []);
  const fioConfig = React.useMemo(() => getFioConfig(), []);

  const validateDate = React.useCallback((value: string) => {
    if (!value || value.length !== 8) {
      return true;
    }

    const dateFormatted = `${value.slice(0, 2)}.${value.slice(2, 4)}.${value.slice(4, 8)}`;
    const age = calculateAge(dateFormatted);

    if (age > 150) {
      return "Введите корректную дату";
    }

    return true;
  }, []);

  const validateDateOfBirth = React.useCallback((value: string) => {
    if (!value || value.length !== 8) {
      return true;
    }

    const dateFormatted = `${value.slice(0, 2)}.${value.slice(2, 4)}.${value.slice(4, 8)}`;
    const age = calculateAge(dateFormatted);

    if (age < 18) {
      return "Страхование доступно с 18 лет";
    }

    if (age > 150) {
      return "Введите корректную дату рождения";
    }

    return true;
  }, []);

  const handleGrzChange = React.useCallback(
    (value: string) => {
      if (grzError && value && value.length >= MIN_GRZ_LENGTH) {
        setGrzError(null);
      }
    },
    [grzError]
  );

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    methods.clearErrors("name");
    const checked = e.target.checked;
    setIsNoSurName(checked);

    store.noSurname.set(checked);

    if (checked && isNoSurNameError) {
      setIsNoSurNameError(undefined);
    }

    if (checked) {
      const currentName = methods.getValues("name");
      if (currentName) {
        const words = currentName
          .trim()
          .split(SPACING_REGULAR)
          .filter((word) => word.length > 0);
        if (words.length > 2) {
          const newName = words.slice(0, 2).join(" ");
          methods.setValue("name", newName);
        }
      }
    }
  };

  const handleExperienceDateSameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsExperienceDateSame(checked);
    if (checked) {
      methods.clearErrors("experienceStartDate");
      const licenseIssueDate = methods.getValues("licenseIssueDate");
      if (licenseIssueDate) {
        methods.setValue("experienceStartDate", licenseIssueDate);
      }
    }
  };

  React.useEffect(() => {
    const grz = store.grz.peek();
    const fio = store.fio.peek();
    const dateOfBirth = store.dateOfBirth.peek();
    const passport = store.passport.peek();
    const passportDate = store.passportDate.peek();
    const experienceStartDate = store.experienceStartDate.peek();
    const phoneNum = store.phoneNum.peek();
    const noSurname = store.noSurname.peek();

    if (grz) {
      methods.setValue("grz", grz);
    }
    if (fio) {
      methods.setValue("name", fio);
    }
    if (dateOfBirth) {
      methods.setValue("dateOfBirth", dateOfBirth.replace(/\./g, ""));
    }
    if (passport) {
      methods.setValue("licenseNumber", passport.replace(/\s/g, ""));
    }
    if (passportDate) {
      methods.setValue("licenseIssueDate", passportDate.replace(/\./g, ""));
    }
    if (experienceStartDate) {
      methods.setValue("experienceStartDate", experienceStartDate.replace(/\./g, ""));
    }
    if (phoneNum) {
      methods.setValue("phone", normalizePhone(phoneNum));
    }
    if (noSurname !== undefined) {
      setIsNoSurName(noSurname);
    }

    if (checkDatesMatch(passportDate, experienceStartDate)) {
      setIsExperienceDateSame(true);
    }
  }, [methods, checkDatesMatch]);

  return (
    <PageLayout className="flex flex-col items-center" header={<Header />}>
      <h1 className="headline2 mt-10 px-5 text-[44px] md:px-8">Данные для оформления</h1>
      <div className="mt-5 mb-32 flex w-full max-w-150 flex-col justify-between rounded-4xl bg-[#FAFAFA] py-10">
        <div className="px-6 md:px-10">
          <p className="headline5 text-[22px] text-primary-headline">Автомобиль</p>
          {selectedCar && !isDrawerOpen ? null : (
            <FormProvider {...methods}>
              <form className="mt-5 flex h-full gap-5" onSubmit={handleSearchCar}>
                <GRZMaskInput
                  {...finuslugiMaskInputProps}
                  className="h-full w-full"
                  control={methods.control}
                  error={grzError || methods.formState.errors.grz?.message || null}
                  key={`${grzKey}-${formUiKey}`}
                  label="Госномер"
                  name="grz"
                  onChange={handleGrzChange}
                  ref={grzInputRef}
                />
                <Button className="hidden h-14 w-39 bg-[#4469C6] md:block" disabled={isFetching} isLoading={isFetching} type="submit">
                  Найти авто
                </Button>
                <Button
                  className="block h-14 min-w-14 bg-[#4469C6] md:hidden"
                  disabled={isFetching}
                  isLoading={isFetching}
                  leftIcon={<IconSearch className="size-5 min-w-5" />}
                  type="submit"
                />
              </form>
            </FormProvider>
          )}

          {Boolean(displayCar && !isFetching && !isDrawerOpen && !wasSearched) && (
            <div className="mt-5 flex w-full items-center gap-4 rounded-2xl bg-white px-5 py-2">
              <IconCar className="size-9 min-w-9 text-button-default" />
              <div>
                <p className="body-regular font-extrabold text-[16px] text-primary-headline">{carName}</p>
                <p className="caption-regular text-[14px] text-secondary">{carRegNumber}</p>
              </div>
              <Button className="ml-auto" onClick={handleClearCar} theme="text">
                <IconClose className="size-6 min-w-6 text-[#BEC9D1]" />
              </Button>
            </div>
          )}
        </div>

        <figure className="my-7 h-px w-full bg-divider-thin" />

        <div className="px-6 md:px-10">
          <p className="headline5 text-[22px] text-primary-headline">Кого вписать в страховку?</p>

          <MaskInput
            {...finuslugiMaskInputProps}
            className="mt-5 rounded-xl"
            config={fioConfig}
            control={methods.control}
            error={methods.formState.errors.name?.message || null}
            key={`name-${formUiKey}`}
            label={isNoSurName ? "ФИ" : "ФИО"}
            name="name"
            ref={nameInputRef}
          />

          <Checkbox
            checked={isNoSurName}
            className="mt-3"
            error={isNoSurNameError}
            id="checkbox"
            label={<p className="body-regular text-[16px] text-primary-headline">Нет отчества</p>}
            onChange={handleTermsChange}
          />

          <MaskInput
            {...finuslugiMaskInputProps}
            className="mt-5 w-full rounded-xl"
            config={dateOfBirthConfig}
            control={methods.control}
            error={methods.formState.errors.dateOfBirth?.message || null}
            inputContainerClassName="md:max-w-50"
            key={`dateOfBirth-${formUiKey}`}
            label="Дата рождения"
            name="dateOfBirth"
            ref={dateOfBirthInputRef}
          />

          <div className="mt-5 flex w-full flex-col gap-5 md:flex-row">
            <MaskInput
              {...finuslugiMaskInputProps}
              className="w-full rounded-xl"
              config={licenseNumberConfig}
              control={methods.control}
              error={methods.formState.errors.licenseNumber?.message || null}
              key={`licenseNumber-${formUiKey}`}
              label="Серия и номер прав"
              name="licenseNumber"
              ref={licenseNumberInputRef}
            />
            <MaskInput
              {...finuslugiMaskInputProps}
              className="input w-full rounded-xl"
              config={dateConfig}
              control={methods.control}
              error={methods.formState.errors.licenseIssueDate?.message || null}
              key={`licenseIssueDate-${formUiKey}`}
              label="Дата выдачи"
              name="licenseIssueDate"
              ref={licenseIssueDateInputRef}
            />
          </div>

          <Checkbox
            checked={isExperienceDateSame}
            className="mt-3"
            id="experience-date-same"
            label={<p className="body-regular text-[16px] text-primary-headline">Дата начала стажа совпадает с датой выдачи прав</p>}
            onChange={handleExperienceDateSameChange}
          />

          {!isExperienceDateSame && (
            <MaskInput
              {...finuslugiMaskInputProps}
              className="mt-5 rounded-xl"
              config={dateConfig}
              control={methods.control}
              error={methods.formState.errors.experienceStartDate?.message || null}
              inputContainerClassName="md:max-w-50"
              key={`experienceStartDate-${formUiKey}`}
              label="Дата начала стажа"
              name="experienceStartDate"
              ref={experienceStartDateInputRef}
            />
          )}
        </div>

        <figure className="my-7 h-px w-full bg-divider-thin" />

        <div className="px-6 md:px-10">
          <p className="headline5 text-[22px] text-primary-headline">Контактные данные клиента</p>
          <MaskInput
            {...finuslugiMaskInputProps}
            className="mt-5 w-full rounded-xl"
            config={phoneConfig}
            control={methods.control}
            error={methods.formState.errors.phone?.message || null}
            key={`phone-${formUiKey}`}
            label="Номер телефона"
            name="phone"
            ref={phoneInputRef}
          />
          {isExistingProfile && !isProfileCreated ? (
            <div className="mt-5 rounded-[20px] bg-white p-5">
              <div className="flex items-center justify-center gap-1">
                <IconNotification className="size-4 min-w-4 text-button-default" />
                <p className="caption-regular text-center text-[14px] text-button-default">Аккаунт уже существует</p>
              </div>
              <p className="caption-regular mt-3 text-center text-[14px] text-primary-headline">
                Клиент с такими данными уже существует. Создать новую заявку <br /> для него не получится.
              </p>
              <Button className="mt-3 h-14 w-full bg-[#4469C6]" onClick={handleRegisterAnotherClient}>
                Зарегистрировать другого клиента
              </Button>
            </div>
          ) : null}
          {!(isProfileCreated || isExistingProfile) && isInsuranceRejected ? (
            <div className="mt-5 rounded-[20px] bg-white p-5">
              <div className="flex items-center justify-center gap-1">
                <IconError className="size-4 min-w-4 text-[#F55600]" />
                <p className="caption-regular text-center text-[14px] text-button-default">Заявка не одобрена</p>
              </div>
              <p className="caption-regular mt-3 text-center text-[14px] text-primary-headline">
                По результатам проверки страховая компания не смогла одобрить заявку
              </p>
              <Button className="mt-3 h-14 w-full bg-[#4469C6]" onClick={handleRegisterAnotherClient}>
                Зарегистрировать другого клиента
              </Button>
            </div>
          ) : null}
          {isProfileCreated || isExistingProfile || isInsuranceRejected ? null : (
            <div className="mt-7">
              <Button className={cn("h-14 w-full bg-[#4469C6]")} disabled={isSendingLink} onClick={handleSendLink}>
                <AnimatePresence initial={false} mode="wait">
                  {isSendingLink ? (
                    <motion.span
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center justify-center gap-2"
                      exit={{ opacity: 0, scale: 0.97 }}
                      initial={{ opacity: 0, scale: 0.97 }}
                      key="loader"
                      transition={{ duration: 0.2 }}
                    >
                      <div className="relative h-5 w-auto min-w-[min(100%,280px)]">
                        <AnimatePresence mode="wait">
                          <motion.div
                            animate={{ opacity: 1, y: 0 }}
                            className="absolute inset-0 flex items-center justify-center whitespace-nowrap"
                            exit={{ opacity: 0, y: -3 }}
                            initial={{ opacity: 0, y: 7 }}
                            key={displayState.key}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                          >
                            <motion.span
                              animate={{ backgroundPosition: "200% 0" }}
                              initial={{ backgroundPosition: "-200% 0" }}
                              style={{
                                backgroundImage:
                                  "linear-gradient(90deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 0.4) 100%)",
                                backgroundSize: "200% 100%",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                display: "inline-block",
                              }}
                              transition={{
                                duration: 4.5,
                                ease: "linear",
                                repeat: Number.POSITIVE_INFINITY,
                              }}
                            >
                              {displayState.text}
                            </motion.span>
                          </motion.div>
                        </AnimatePresence>
                      </div>
                    </motion.span>
                  ) : (
                    <motion.span
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center justify-center gap-2"
                      exit={{ opacity: 0, scale: 0.97 }}
                      initial={{ opacity: 0, scale: 0.97 }}
                      key="content"
                      transition={{ duration: 0.2 }}
                    >
                      Создать профиль
                    </motion.span>
                  )}
                </AnimatePresence>
              </Button>
            </div>
          )}
          {isProfileCreated ? <ProfileCreatedShareCard /> : null}
        </div>
      </div>

      <CarConfirmationDrawer
        carData={carData}
        error={carError}
        forceShowInvalid={showRatesInvalid}
        grzValue={grzValue}
        isFetched={isFetched}
        isFetching={isFetching}
        onConfirm={handleCarConfirm}
        onOpenChange={(open) => {
          if (!open) {
            setShowRatesInvalid(false);
          }
          setIsDrawerOpen(open);
        }}
        onSearchAgain={() => grzInputRef.current?.focus()}
        onWasSearchedChange={setWasSearched}
        open={isDrawerOpen}
        wasSearched={wasSearched}
      />
      <SendLinkErrorDrawer onOpenChange={setIsErrorDrawerOpen} onRetry={handleRetrySendLink} open={isErrorDrawerOpen} />
    </PageLayout>
  );
}
